import { GenericModelApi } from "./GenericModelApi";
import { ApiSchema, FetchResponse } from "./ApiClient";
import { CreditnoteProductApi, CreditnoteProductDataType } from "./CreditnoteProductApi";
import { CreditnoteFormData, CreditnoteRelatedAttributes } from "@/formTypes/CreditnoteDataType";
import { CreditnoteFormDataKeys } from "@/generated/CreditnoteFormDataKeys";
import { omitProperties } from "@utils/Utils";
import { DocumentSequenceApi } from "./DocumentSequenceApi";
import { DocumentAutoGeneratedNumberApi } from "./DocumentAutoGeneratedNumberApi";
import { ProductApi } from "./ProductsApi";
import { saveEDocumentViaLambda } from "./EDocumentSaveApi";

export type CreditnoteDataType = Partial<ApiSchema["Creditnote"]["type"] & CreditnoteFormData>;

class Api extends GenericModelApi<"Creditnote", CreditnoteDataType> {
    protected userSortKeyDateField = "created" as const

    async fetchAll(filters: any = {}, token?: string, limit: number = 20): Promise<FetchResponse<CreditnoteDataType>> {
        const selectionSet = [
            ...CreditnoteFormDataKeys.filter(k => !CreditnoteRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "creditnoteProducts.*",
            "invoice.*"
        ]

        return super.fetchAll(filters, token, limit, selectionSet);
    }

    /**
     * Claves de Hacienda de todas las notas del tenant, para detectar duplicados en la carga
     * masiva. Trae solo `id` y `document_key` porque son miles de registros.
     */
    async fetchAllDocumentKeys(): Promise<Set<string>> {
        const keys = new Set<string>();
        let token: string | undefined = undefined;

        do {
            const { items, nextToken }: FetchResponse<CreditnoteDataType> = await super.fetchAll(
                {},
                token,
                1000,
                ["id", "document_key"],
                { noCache: true }
            );
            items.forEach((creditnote) => {
                if (creditnote.document_key) keys.add(`${creditnote.document_key}`);
            });
            token = nextToken;
        } while (token);

        return keys;
    }

    /**
     * Guardado transaccional en una sola llamada (lambda saveEDocument).
     * need_assignment lo calcula la lambda leyendo la factura vinculada.
     */
    async createCreditnoteAndProducts(input: CreditnoteDataType, products: CreditnoteProductDataType[], increaseStock: boolean = true): Promise<CreditnoteDataType> {
        const lines = products.map((p) => omitProperties({ ...p }, ["id", "forceRecalculatePrice"], true))

        const data = await saveEDocumentViaLambda<CreditnoteDataType>({
            documentType: "Creditnote",
            document: { ...input, price_note: !increaseStock } as Record<string, unknown>,
            lines,
            stockMode: increaseStock ? "increase" : "none",
        })

        this.invalidateAllByTenant()
        this.clearCache()
        CreditnoteProductApi.invalidateAllByTenant()
        ProductApi.invalidateAllByTenant()
        DocumentSequenceApi.invalidateAllByTenant()
        DocumentAutoGeneratedNumberApi.invalidateAllByTenant()

        return data;
    }

    async fetchAllByPeriod(startData: string, endData: string, filters: any = {}, token?: string, limit: number = 500): Promise<FetchResponse<InvoiceDataType>> {
        const selectionSet = [
            ...CreditnoteFormDataKeys.filter(k => !CreditnoteRelatedAttributes.includes(k)),
            "currency.*",
            "client.*",
            "invoice.*",
            "invoice.client.*",
            "invoice.currency.*",
            "creditnoteProducts.*"
        ]
        return await super.fetchAllByPeriod("listCreditnoteByFechaTenant", startData, endData, filters, token, {
            selectionSet,
            limit
        })
    }

}
const api = new Api("Creditnote", "numero", "DESC", "numero")
api.setCreateDataFieldName("created")
export const CreditnoteApi = api